'use client';

import { Css, Responsive } from '@/ui';
import { css } from '@emotion/core';
import { usePathname } from 'next/navigation';
import styled from '@emotion/styled';
import { CSSProperties } from 'react';
import { Color } from '../../../styles/Color';
import { RouterLink } from '../link/RouterLink';
import SearchIcon from '../../../assets/icons/search.svg';
import { Shadow } from '../../../styles/Shadow';
import { PageRoutes } from '../../../features/app/pages/domain/constants/PageRoutes';
import { useElementWidth } from '../../../hooks/useElementWidth';

export default function SearchButton({ expanded }: { expanded?: boolean }) {
	const pathname = usePathname();
	const isActive = pathname === `${PageRoutes.Search}/`;
	const { width, ref } = useElementWidth<HTMLDivElement>();

	return (
		<StyledLink
			href={PageRoutes.Search}
			aria-label="Search"
			aria-pressed={isActive}
			aria-current={isActive}
			aria-expanded={expanded}
		>
			<SearchContentWrap
				isActive={isActive}
				expanded={!!expanded}
				ref={ref}
				style={
					{ '--link-width': width ? `${width}px` : undefined } as CSSProperties
				}
			>
				<SearchIcon width={24} height={24} />

				<SearchButtonLabel>Search</SearchButtonLabel>
			</SearchContentWrap>
		</StyledLink>
	);
}

const StyledLink = styled(RouterLink)`
	display: flex;
	align-items: center;
	justify-content: center;
	text-decoration: none;
	background-color: ${Color.PrimaryWhite};
	border-radius: 54px;
	height: fit-content;
	margin-top: ${Css.pxToRem(16)};
	box-shadow: ${Shadow.Small};
	pointer-events: all;

	svg {
		path {
			fill: currentColor;
		}
	}

	:hover {
		text-decoration: underline;
		color: ${Color.OnBackgroundText};

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			text-decoration: none;
		}
	}

	&[aria-current='page'] {
		color: ${Color.PrimaryBlue};
	}

	&:focus,
	&:active {
		color: ${Color.PrimaryBlue};
		svg {
			path {
				fill: ${Color.PrimaryBlue};
			}
		}
	}

	&:not([aria-current='page']):hover {
		text-decoration: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: 0;
		box-shadow: ${Shadow.Small};
		height: 34px;
		width: 34px;

		svg {
			path {
				fill: ${Color.PrimaryBlue};
			}
		}
	}
`;

const SearchButtonLabel = styled.div`
	transition: opacity 0.3s;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		display: none;
	}
`;

const SearchContentWrap = styled.div<{ isActive: boolean; expanded: boolean }>`
	display: flex;
	align-items: center;
	gap: ${Css.pxToRem(4)};
	padding: ${Css.pxToRem(8)} ${Css.pxToRem(16)};
	border-radius: 80px;
	margin: ${Css.pxToRem(8)};
	font-weight: 400;
	color: ${({ isActive }) =>
		isActive ? Color.PrimaryBlue : Color.SecondaryBlue1};

	${({ isActive }) =>
		isActive &&
		css`
			background-color: ${Color.PrimaryBlue97};
		`}

	transition: max-width 0.35s cubic-bezier(0.65, 0, 0.35, 1);
	max-width: ${({ expanded }) => (expanded ? '160px' : '56px')};
	overflow: hidden;

	${SearchButtonLabel} {
		opacity: ${({ expanded }) => (expanded ? 1 : 0)};
	}

	&:focus,
	&:active {
		background-color: ${Color.PrimaryBlue97};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: 0;
		margin: 0;
		width: 100%;
		height: 100%;
		max-width: unset;
		justify-content: center;
	}

	svg {
		flex-shrink: 0;
	}
`;
